import cn from 'classnames'
import { useEffect, useRef, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'

import { PLANNING_SMB_TRANSLATION_KEYS, SMB_FY_QUARTER } from '../../../../configs'
import { ISmbRow, isSmbNodeExcluded } from '../../../../lib'
import { ISmbColumn, ISmbNode, TSmbSplitResult } from '../../../../model/types'
import { PlanningSmbCellStack } from '../planningSmbCellStack/PlanningSmbCellStack'

import styles from './PlanningSmbGrid.module.scss'

const KEYS = PLANNING_SMB_TRANSLATION_KEYS.modal

export interface IPlanningSmbGridProps {
  rows: ISmbRow[]
  columns: ISmbColumn[]
  /** Row requested by the Plan / Update plan action; briefly highlighted on modal open. */
  focusNodeId?: string
  /** True while the plan year is broken down into quarters */
  isQuarterly: boolean
  /** Shows the Financial Plan and Baseline amounts split between SMB and EU. */
  showFinancialData: boolean
  /** The plan year itself - the only one that can be planned by quarter */
  fYear: number
  disabled?: boolean
  getWeight: (node: ISmbNode, year: number, fq: number) => number
  getBaselineWeight: (node: ISmbNode, fq: number) => number
  getCellValue: (node: ISmbNode, year: number, fq: number) => TSmbSplitResult
  getBaselineValue: (node: ISmbNode, fq: number) => TSmbSplitResult
  formatMoney: (value: number) => string
  onToggleExpanded: (nodeId: string) => void
  onCommit: (nodeId: string, year: number, fq: number, smb: number) => void
}

/** FY26, FY27 ... - the label the planning widget uses for a fiscal year */
const getYearLabel = (year: number) => `FY${`${year}`.slice(-2)}`

export const PlanningSmbGrid = ({
  rows,
  columns,
  focusNodeId,
  isQuarterly,
  showFinancialData,
  fYear,
  disabled,
  getWeight,
  getBaselineWeight,
  getCellValue,
  getBaselineValue,
  formatMoney,
  onToggleExpanded,
  onCommit,
}: IPlanningSmbGridProps) => {
  const t = useSafetyTranslation()
  const focusRowRef = useRef<HTMLTableRowElement>(null)
  const [flashingNodeId, setFlashingNodeId] = useState(focusNodeId)
  const labelWidth = isQuarterly ? 15 : 22
  const dataWidth = (100 - labelWidth) / columns.length

  useEffect(() => {
    setFlashingNodeId(focusNodeId)
    if (!focusNodeId) {
      return
    }

    focusRowRef.current?.scrollIntoView?.({ block: 'center' })
    const timeoutId = window.setTimeout(() => setFlashingNodeId(undefined), 1400)

    return () => window.clearTimeout(timeoutId)
  }, [focusNodeId])

  const isQuarterlyYear = (year: number) => year === fYear && isQuarterly

  const renderColumnLabel = (column: ISmbColumn) => {
    if (column.fq !== SMB_FY_QUARTER) {
      return <>{`Q${column.fq}`}</>
    }

    return (
      <>
        {getYearLabel(column.year)}{' '}
        <span className={styles.finHint}>
          <Translation
            span
            text={isQuarterlyYear(column.year) ? KEYS.columnFyWeighted : KEYS.columnFySplit}
          />
        </span>
      </>
    )
  }

  return (
    <table className={cn(styles.grid, { [styles.gridQuarterly]: isQuarterly })}>
      <colgroup>
        <col style={{ width: `${labelWidth}%` }} />
        {columns.map((column) => (
          <col key={`${column.year}-${column.fq}`} style={{ width: `${dataWidth}%` }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th scope="col">
            <Translation span text={KEYS.columnHierarchy} />
          </th>
          {columns.map((column) => (
            <th
              key={`${column.year}-${column.fq}`}
              scope="col"
              className={cn(styles.dataHeading, {
                [styles.yearStart]: column.isYearStart,
              })}
            >
              {renderColumnLabel(column)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.node.id}
            ref={row.node.id === focusNodeId ? focusRowRef : undefined}
            data-row-node={row.node.id}
            className={cn(styles.row, styles[`depth${Math.min(row.depth, 3)}`], {
              [styles.headerRow]: row.isExpanded,
              [styles.offeringRow]: row.node.kind === 'offering',
              [styles.rowFlash]: row.node.id === flashingNodeId,
            })}
          >
            <th scope="row">
              <div className={styles.label} style={{ paddingLeft: `${row.depth * 16}px` }}>
                {row.canExpand ? (
                  <button
                    type="button"
                    className={styles.toggle}
                    aria-expanded={row.isExpanded}
                    aria-label={`${t(row.isExpanded ? KEYS.collapse : KEYS.expand)} ${t(
                      row.node.nameKey
                    )}`}
                    disabled={disabled}
                    onClick={() => onToggleExpanded(row.node.id)}
                  >
                    {row.isExpanded ? '▾' : '▸'}
                  </button>
                ) : (
                  <span className={styles.toggleSpacer} />
                )}
                <span className={cn(styles.name, { [styles.rootName]: row.depth === 0 })}>
                  <Translation span text={row.node.nameKey} />
                </span>
                {row.node.kind === 'offering' && (
                  <span className={styles.offeringTag}>
                    <Translation span text={KEYS.offeringTotal} />
                  </span>
                )}
              </div>
            </th>
            {isSmbNodeExcluded(row.node) ? (
              // The row is out of the plan - its category is Not applicable in Planning, or the
              // row is disabled on the back end: one message instead of the whole year/quarter
              // grid, and its own product tree stays hidden.
              <td className={styles.notApplicable} colSpan={columns.length}>
                <Translation span text={row.node.isDisabled ? KEYS.disabled : KEYS.notApplicable} />
              </td>
            ) : (
              columns.map((column) => {
                const weight = getWeight(row.node, column.year, column.fq)
                const hasBaseline = column.year === fYear

                return (
                  <td
                    key={`${column.year}-${column.fq}`}
                    className={cn({ [styles.yearStart]: column.isYearStart })}
                  >
                    <PlanningSmbCellStack
                      planLabel={
                        <>
                          {formatMoney(weight)} <Translation span text={KEYS.plan} />
                        </>
                      }
                      weight={weight}
                      value={getCellValue(row.node, column.year, column.fq)}
                      baseline={hasBaseline ? getBaselineValue(row.node, column.fq) : undefined}
                      baselineWeight={
                        hasBaseline ? getBaselineWeight(row.node, column.fq) : undefined
                      }
                      baselineLabel={hasBaseline ? getYearLabel(fYear - 1) : undefined}
                      // An expanded row is calculated from what is below it, never typed into,
                      // and so is the year column of a year that is broken down by quarter.
                      isEditable={
                        !row.isExpanded &&
                        !(isQuarterlyYear(column.year) && column.fq === SMB_FY_QUARTER)
                      }
                      inputAriaLabel={`SMB ${t(row.node.nameKey)} ${getYearLabel(column.year)}${
                        column.fq === SMB_FY_QUARTER ? '' : ` Q${column.fq}`
                      }`}
                      showFinancialData={showFinancialData}
                      disabled={disabled}
                      formatMoney={formatMoney}
                      onCommit={(smb) => onCommit(row.node.id, column.year, column.fq, smb)}
                    />
                  </td>
                )
              })
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
